import { sql } from 'drizzle-orm';
import { pgTable, uuid, varchar, text, boolean, timestamp, uniqueIndex, index, check, jsonb, integer } from 'drizzle-orm/pg-core';

export const organizations = pgTable('organizations', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  name: varchar('name', { length: 255 }).notNull(),
  slug: varchar('slug', { length: 100 }).notNull(),
  isActive: boolean('is_active').notNull().default(true),
  // Company setup (MIS-261). Validated in shared/firmProfile.ts; the CHECKs keep the same values at the database.
  legalName: varchar('legal_name', { length: 255 }),
  legalEntityType: varchar('legal_entity_type', { length: 32 }),
  dba: varchar('dba', { length: 255 }),
  // The EIN is stored encrypted (AES-256-GCM, server/utils/fieldEncryption.ts); only its last four digits are kept in the clear, for the masked display.
  einEncrypted: text('ein_encrypted'),
  einLastFour: varchar('ein_last_four', { length: 4 }),
  // Email setup (MIS-262): 'gmail' or 'manual' (no email sync). Null until the firm chooses.
  emailSyncMode: varchar('email_sync_mode', { length: 16 }),
  stripeCustomerId: varchar('stripe_customer_id', { length: 255 }),
  stripeSubscriptionId: varchar('stripe_subscription_id', { length: 255 }),
  stripePriceId: varchar('stripe_price_id', { length: 255 }),
  billingInterval: varchar('billing_interval', { length: 16 }).notNull().default('monthly'),
  billingStatus: varchar('billing_status', { length: 32 }).notNull().default('none'),
  cardSavedAt: timestamp('card_saved_at'),
  trialEndsAt: timestamp('trial_ends_at'),
  trialStartAt: timestamp('trial_start_at'),
  firstChargeAt: timestamp('first_charge_at'),
  billingSeats: integer('billing_seats'),
  billingContactName: varchar('billing_contact_name', { length: 255 }),
  billingContactEmail: varchar('billing_contact_email', { length: 320 }),
  cardAuthStatus: varchar('card_auth_status', { length: 16 }),
  cardAuthAt: timestamp('card_auth_at'),
  gracePeriodEndsAt: timestamp('grace_period_ends_at'),
  lockedAt: timestamp('locked_at'),
  foundingRateLocked: boolean('founding_rate_locked').notNull().default(false),
  createdViaBetaSignup: boolean('created_via_beta_signup').notNull().default(false),
  cardRequired: boolean('card_required').notNull().default(false),
  onboardingStep: varchar('onboarding_step', { length: 32 }).notNull().default('create_firm'),
  inviteStepSkipped: boolean('invite_step_skipped').notNull().default(false),
  firstClaimCreatedAt: timestamp('first_claim_created_at'),
  // Scan the firm's Gmail for claims after it connects (docs/first-claim-from-email.md). Off for every existing firm;
  // the beta signup (POST /api/onboarding/firm) turns it on for a new firm.
  firstClaimScanEnabled: boolean('first_claim_scan_enabled').notNull().default(false),
  walkthroughChecklist: jsonb('walkthrough_checklist'),
  cardPromptDismissedAt: timestamp('card_prompt_dismissed_at'),
  betaCodeUsed: varchar('beta_code_used', { length: 64 }),
  requireMfaForAdjusters: boolean('require_mfa_for_adjusters').notNull().default(false),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => [
  uniqueIndex('organizations_slug_unique').on(table.slug),
  check(
    'organizations_legal_entity_type_check',
    sql`${table.legalEntityType} IS NULL OR ${table.legalEntityType} IN ('llc', 'corporation', 's_corporation', 'sole_proprietorship', 'partnership')`,
  ),
  check('organizations_ein_last_four_check', sql`${table.einLastFour} IS NULL OR ${table.einLastFour} ~ '^[0-9]{4}$'`),
  check('organizations_ein_pair_check', sql`(${table.einEncrypted} IS NULL) = (${table.einLastFour} IS NULL)`),
  check('organizations_email_sync_mode_check', sql`${table.emailSyncMode} IS NULL OR ${table.emailSyncMode} IN ('gmail', 'manual')`),
]);

export const orgDomainMappings = pgTable('org_domain_mappings', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  orgId: uuid('org_id').notNull().references(() => organizations.id, { onDelete: 'cascade' }),
  domain: varchar('domain', { length: 255 }).notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
}, (table) => [
  uniqueIndex('org_domain_mappings_domain_unique').on(table.domain),
]);

// Firm locations (MIS-261): a firm's offices, one marked primary. Firm-scoped by org_id (RLS: drizzle/0080).
export const firmLocations = pgTable('firm_locations', {
  id: uuid('id').primaryKey().default(sql`gen_random_uuid()`),
  orgId: uuid('org_id').notNull().references(() => organizations.id, { onDelete: 'cascade' }),
  name: varchar('name', { length: 120 }),
  addressLine1: varchar('address_line1', { length: 200 }).notNull(),
  addressLine2: varchar('address_line2', { length: 200 }),
  city: varchar('city', { length: 100 }).notNull(),
  state: varchar('state', { length: 2 }).notNull(),
  zip: varchar('zip', { length: 10 }).notNull(),
  timeZone: varchar('time_zone', { length: 64 }).notNull(),
  isPrimary: boolean('is_primary').notNull().default(false),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
}, (table) => [
  index('idx_firm_locations_org').on(table.orgId),
  uniqueIndex('firm_locations_one_primary_per_org').on(table.orgId).where(sql`${table.isPrimary}`),
]);

export type FirmLocationRow = typeof firmLocations.$inferSelect;
export type InsertFirmLocationRow = typeof firmLocations.$inferInsert;
